import * as THREE from 'three';
import { clamp, damp, lerp, smoothstep } from '../world/noise';
import { BLADE_TIP, soriAt } from './Accessories';

/**
 * Drawn-katana layer on top of the locomotion clips: draw from the back saya, a three-hit combo,
 * auto-sheathe. Everything is procedural: each pose names where the fist and blade should be in
 * model space (metres, +Z forward, +X the character's left), two-bone IK places the arms, and the
 * sword follows the right hand except while it slides along the saya.
 */

// Shared scratch.
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();
const _z = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _m2 = new THREE.Matrix4();
const _t0 = new THREE.Vector3();
const _t1 = new THREE.Vector3();
const _tip = new THREE.Vector3();
const _edge = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _q3 = new THREE.Quaternion();
const _qa = new THREE.Quaternion();
const _qf = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _p = new THREE.Vector3();
const _p2 = new THREE.Vector3();
const _e = new THREE.Vector3();
const _u = new THREE.Vector3();
const _f = new THREE.Vector3();
const _n = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _pole = new THREE.Vector3();
const _one = new THREE.Vector3(1, 1, 1);
const _euler = new THREE.Euler();

export type SwordEvent =
  | { type: 'draw'; clearIn: number }
  | { type: 'swing'; hit: number; contactIn: number }
  | { type: 'contact'; hit: number }
  | { type: 'sheathe'; seatIn: number }
  | { type: 'seat' };

export type SwordContext = { speed: number; flipping: boolean };

type Phase = 'sheathed' | 'draw' | 'present' | 'windup' | 'swing' | 'recover' | 'ready' | 'sheathe';

const GRIP_TO_TSUBA = 0.055;
/** Slide from seated until the kissaki clears the koiguchi. */
const DRAW_OUT = 0.74;
/** The saya dips along its own axis while the blade leaves it, so the arm can reach. */
const SAYA_DROP = 0.16;
const DRAW_REACH = 0.12;
const DRAW_PULL = 0.11;
const DRAW_WINDUP = 0.1;
const SHEATHE_KEYS = [0.16, 0.14, 0.14];
const SHEATHE_LINEUP = SHEATHE_KEYS[0] + SHEATHE_KEYS[1] + SHEATHE_KEYS[2];
const SHEATHE_SLIDE = 0.24;
const SHEATHE_SEAT = SHEATHE_LINEUP + SHEATHE_SLIDE;
const SHEATHE_RELEASE = 0.28;
/** Drawn without attacking: from the saya straight into the held guard. */
const PRESENT_TIME = 0.24;
const CHAIN_WINDOW = 0.6;
/** Fraction of a swing after which a press is kept and starts the next hit when the swing settles. */
const QUEUE_FROM = 0;
/** Swing progress (time) with the fastest blade: the profile below peaks at 1/3. */
const CONTACT_U = 1 / 3;

/** Blade speed profile, the regularised incomplete beta I(3, 5): speed peaks a third of the way in, then a long settle. */
function swingProfile(u: number): number {
  const x = clamp(u, 0, 1);
  const y = 1 - x;
  const x3 = x * x * x;
  const y2 = y * y;
  return 35 * x3 * y2 * y2 + 35 * x3 * x * y2 * y + 21 * x3 * x * x * y2 + 7 * x3 * x3 * y + x3 * x3 * x;
}

const easeInOut = (t: number) => {
  const x = clamp(t, 0, 1);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
};
const easeIn = (t: number) => {
  const x = clamp(t, 0, 1);
  return x * x * x;
};

const v3 = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/** Sword orientation whose tip (-Y) points along `tip` with the edge (+Z) toward `edge`. */
function swordQuat(tip: THREE.Vector3, edge: THREE.Vector3, out: THREE.Quaternion): THREE.Quaternion {
  _y.copy(tip).normalize().negate();
  _z.copy(edge).addScaledVector(_y, -edge.dot(_y)).normalize();
  _x.crossVectors(_y, _z);
  _m.makeBasis(_x, _y, _z);
  return out.setFromRotationMatrix(_m);
}

class Pose {
  readonly grip = new THREE.Vector3();
  readonly quat = new THREE.Quaternion();
  readonly pole = new THREE.Vector3(-1, -1, 0).normalize();
  readonly left = new THREE.Vector3(0.12, 1.0, 0.16);
  /** x = spine twist (+ turns left), y = spine bend (+ forward), z = crouch 0..1. */
  readonly body = new THREE.Vector3();
  leftW = 0;

  copy(o: Pose): this {
    this.grip.copy(o.grip);
    this.quat.copy(o.quat);
    this.pole.copy(o.pole);
    this.left.copy(o.left);
    this.body.copy(o.body);
    this.leftW = o.leftW;
    return this;
  }

  blend(a: Pose, b: Pose, t: number): this {
    this.grip.lerpVectors(a.grip, b.grip, t);
    this.quat.slerpQuaternions(a.quat, b.quat, t);
    this.pole.lerpVectors(a.pole, b.pole, t).normalize();
    this.left.lerpVectors(a.left, b.left, t);
    this.body.lerpVectors(a.body, b.body, t);
    this.leftW = lerp(a.leftW, b.leftW, t);
    return this;
  }

  set(grip: THREE.Vector3, tip: THREE.Vector3, edge: THREE.Vector3, pole: THREE.Vector3, twist = 0, bend = 0, crouch = 0): this {
    this.grip.copy(grip);
    swordQuat(tip, edge, this.quat);
    this.pole.copy(pole).normalize();
    this.body.set(twist, bend, crouch);
    this.leftW = 0;
    return this;
  }
}

type SwingKey = { g: number; grip: THREE.Vector3; pole: THREE.Vector3; body: THREE.Vector3; left: THREE.Vector3; leftW: number };

type Swing = {
  windup: number;
  swing: number;
  recover: number;
  /** Blade rotates about `axis` from `a0` to `a1`; `tip0` is the tip direction at angle 0. */
  axis: THREE.Vector3;
  tip0: THREE.Vector3;
  a0: number;
  a1: number;
  keys: SwingKey[];
  /** Whole-body turn over the swing (radians), for the spin. */
  spin: number;
};

type V = [number, number, number];

const key = (g: number, grip: V, pole: V, twist: number, bend: number, crouch: number, left: V = [0.12, 1.0, 0.16], leftW = 0.65): SwingKey => ({
  g,
  grip: v3(...grip),
  pole: v3(...pole).normalize(),
  body: v3(twist, bend, crouch),
  left: v3(...left),
  leftW,
});

/** Kesa-giri: from beside the right ear, down across the body to the left hip. */
const KESA: Swing = {
  windup: 0.14,
  swing: 0.46,
  recover: 0.32,
  axis: v3(1, 1, 0).normalize(),
  tip0: v3(0, 0, 1),
  a0: -2.2,
  a1: 2.35,
  spin: 0,
  keys: [
    key(0, [-0.31, 1.55, 0.0], [-1, 0.15, 0.25], -0.38, -0.04, 0.2),
    key(0.43, [-0.12, 1.3, 0.42], [-0.6, -0.8, 0.0], 0.05, 0.12, 0.6),
    key(0.75, [0.08, 1.02, 0.36], [-0.3, -0.9, -0.3], 0.32, 0.2, 0.8),
    key(1, [0.14, 0.95, 0.24], [-0.4, -0.6, -0.7], 0.42, 0.16, 0.6),
  ],
};

/** Gyaku kiriage: backhand rising cut from the left hip up past the right shoulder. */
const KIRIAGE: Swing = {
  windup: 0.1,
  swing: 0.44,
  recover: 0.32,
  axis: v3(-0.45, -0.9, 0).normalize(),
  tip0: v3(0, 0, 1),
  a0: -2.3,
  a1: 2.0,
  spin: 0,
  keys: [
    key(0, [0.14, 0.97, 0.22], [-0.2, -0.8, 0.5], 0.45, 0.18, 0.7),
    key(0.43, [-0.04, 1.08, 0.46], [-0.5, -0.85, 0.1], 0.05, 0.1, 0.6),
    key(0.75, [-0.27, 1.28, 0.32], [-0.8, -0.5, -0.2], -0.3, 0.0, 0.4),
    key(1, [-0.33, 1.4, 0.14], [-0.9, -0.2, -0.3], -0.4, -0.04, 0.2),
  ],
};

const SPIN_A0 = -1.9;
const SPIN_A1 = 1.1;
const spinAt = (g: number) => SPIN_A0 + (SPIN_A1 - SPIN_A0) * g;
const spinGrip = (phi: number, r: number, y: number): V => [Math.sin(phi) * r, y, Math.cos(phi) * r];
const spinPole = (phi: number): V => {
  const p = v3(-0.3, -0.85, -0.4).applyAxisAngle(_up, phi);
  return [p.x, p.y, p.z];
};
const SPIN_R = [0.45, 0.52, 0.55, 0.5, 0.42];
const SPIN_Y = [1.0, 0.9, 0.86, 0.9, 0.98];

/** Mawari-giri: a full turn with the blade low and level, cutting a ring through the field. */
const MAWARI: Swing = {
  windup: 0.14,
  swing: 0.62,
  recover: 0.4,
  axis: v3(0, 1, 0),
  tip0: v3(0, -0.72, 0.7).normalize(),
  a0: SPIN_A0,
  a1: SPIN_A1,
  spin: Math.PI * 2,
  keys: SPIN_R.map((r, i) => {
    const g = i / (SPIN_R.length - 1);
    return key(g, spinGrip(spinAt(g), r, SPIN_Y[i]), spinPole(spinAt(g)), lerp(-0.5, 0.45, smoothstep(0, 1, g)), 0.12, 0.85, [0.5, 1.22, 0.05], 0.8);
  }),
};

const HITS = [KESA, KIRIAGE, MAWARI];

/** Non-uniform Catmull-Rom through the keys, sampled at `g`. */
function hermite(keys: SwingKey[], g: number, pick: (k: SwingKey) => THREE.Vector3, out: THREE.Vector3): THREE.Vector3 {
  let i = 0;
  while (i < keys.length - 2 && g > keys[i + 1].g) i++;
  const k0 = keys[i];
  const k1 = keys[i + 1];
  const span = k1.g - k0.g;
  const t = clamp((g - k0.g) / span, 0, 1);
  const p0 = pick(k0);
  const p1 = pick(k1);
  const prev = keys[Math.max(i - 1, 0)];
  const next = keys[Math.min(i + 2, keys.length - 1)];
  _t0.subVectors(p1, pick(prev)).multiplyScalar(span / Math.max(k1.g - prev.g, 1e-4));
  _t1.subVectors(pick(next), p0).multiplyScalar(span / Math.max(next.g - k0.g, 1e-4));
  const t2 = t * t;
  const t3 = t2 * t;
  return out
    .copy(p0)
    .multiplyScalar(2 * t3 - 3 * t2 + 1)
    .addScaledVector(_t0, t3 - 2 * t2 + t)
    .addScaledVector(p1, -2 * t3 + 3 * t2)
    .addScaledVector(_t1, t3 - t2);
}

function swingPose(hit: Swing, g: number, out: Pose): Pose {
  _tip.copy(hit.tip0).applyAxisAngle(hit.axis, lerp(hit.a0, hit.a1, g));
  // The edge leads: it faces the direction the tip travels.
  _edge.crossVectors(hit.axis, _tip);
  if (_edge.lengthSq() < 1e-6) _edge.set(0, -1, 0);
  swordQuat(_tip, _edge, out.quat);
  hermite(hit.keys, g, (k) => k.grip, out.grip);
  hermite(hit.keys, g, (k) => k.pole, out.pole).normalize();
  hermite(hit.keys, g, (k) => k.left, out.left);
  hermite(hit.keys, g, (k) => k.body, out.body);
  let i = 0;
  while (i < hit.keys.length - 2 && g > hit.keys[i + 1].g) i++;
  out.leftW = lerp(hit.keys[i].leftW, hit.keys[i + 1].leftW, smoothstep(hit.keys[i].g, hit.keys[i + 1].g, g));
  return out;
}

// Grip in the right hand's frame (unit axes, metres). Hand local: -X toward the fingers, -Y the palm, +Z the thumb side.
const FIST = new THREE.Vector3(-0.1, -0.023, -0.007);
const GRIP_TILT = 0.34;
const GRIP_TIP = new THREE.Vector3(-Math.sin(GRIP_TILT), 0, Math.cos(GRIP_TILT));
const GRIP_QUAT = swordQuat(GRIP_TIP, new THREE.Vector3(-1, 0, 0), new THREE.Quaternion());
const GRIP_QUAT_INV = GRIP_QUAT.clone().invert();
/** Tsuba (sword origin) in the hand frame. */
const GRIP_POS = FIST.clone().addScaledVector(GRIP_TIP, GRIP_TO_TSUBA);

const FINGERS = ['Index', 'Middle', 'Ring', 'Pinky'] as const;
const CURL: Record<(typeof FINGERS)[number], V> = {
  Index: [0.8, 1.0, 0.55],
  Middle: [0.95, 1.1, 0.6],
  Ring: [1.0, 1.1, 0.6],
  Pinky: [1.05, 1.05, 0.6],
};

export class SwordRig {
  private phase: Phase = 'sheathed';
  private time = 0;
  private hit = 0;
  private chain = 0;
  private queued = false;
  private readyTime = 0;
  private windupTime = 0;
  private fromSaya = false;
  private drawOnly = false;
  /** Seconds of idle before the sword goes back on its own; Infinity keeps it out until toggled. */
  autoSheathe = Infinity;
  private readonly pose = new Pose();
  private readonly start = new Pose();
  private readonly target = new Pose();
  private readonly held = new Pose();
  private readonly readyPose = new Pose();
  private readonly runPose = new Pose();
  private readonly raisedPose = new Pose();
  private readonly overPose = new Pose();
  /** Right-arm IK weight and spine weight. */
  private armW = 0;
  private bodyW = 0;
  /** How much the sword follows the hand rather than the saya axis. */
  private attach = 0;
  /** Blade slid out of the saya (metres) for the saya-axis pose; saya dip 0..1. */
  private slide = 0;
  private sayaDrop = 0;
  /** Deferred blend toward the hilt pose, resolved after the spine moves the socket. */
  private hiltMix = 0;
  private readonly sayaRest = new THREE.Vector3();
  /** Visual yaw added to the body for the spin (radians). */
  spinYaw = 0;
  readonly events: SwordEvent[] = [];
  /** Blade sample points in world space this frame and last frame. */
  readonly tip = new THREE.Vector3();
  readonly base = new THREE.Vector3();
  readonly prevTip = new THREE.Vector3();
  readonly prevBase = new THREE.Vector3();
  tipSpeed = 0;
  private hasPrev = false;
  private readonly swordPos = new THREE.Vector3();
  private readonly swordRot = new THREE.Quaternion();
  private readonly socketPos = new THREE.Vector3();
  private readonly socketQuat = new THREE.Quaternion();
  private readonly modelQuat = new THREE.Quaternion();
  private readonly modelPos = new THREE.Vector3();
  private readonly swordMatrix = new THREE.Matrix4();

  constructor(
    private readonly bones: Record<string, THREE.Bone>,
    private readonly model: THREE.Object3D,
    private readonly root: THREE.Object3D,
    private readonly saya: THREE.Object3D,
    private readonly sword: THREE.Object3D,
    private readonly blade: THREE.Object3D,
  ) {
    this.sayaRest.copy(saya.position);
    sword.matrixAutoUpdate = false;
    blade.visible = false;
    // Arm hangs relaxed with the blade forward and low: the hammer grip's natural line, no bent wrist.
    this.readyPose.set(v3(-0.25, 0.9, 0.1), v3(-0.14, -0.4, 0.9), v3(0, -1, -0.4), v3(-0.35, 0.05, -1));
    this.runPose.set(v3(-0.27, 0.9, 0.04), v3(-0.3, -0.62, 0.72), v3(0, -1, -0.8), v3(-0.35, 0.1, -1));
    this.raisedPose.set(v3(-0.28, 1.36, 0.3), v3(-0.12, 0.96, 0.25), v3(-0.9, 0, 0.3), v3(-0.8, -0.5, 0.2), -0.1, 0, 0.15);
    this.overPose.set(v3(-0.4, 1.72, -0.06), v3(0.15, 0.1, -0.98), v3(-0.3, -0.95, 0.05), v3(-0.8, 0.3, 0.4), -0.18, 0, 0.15);
  }

  get state(): string {
    return this.phase === 'windup' || this.phase === 'swing' || this.phase === 'recover' ? `${this.phase}-${this.hit + 1}` : this.phase;
  }

  get drawn(): boolean {
    return this.phase !== 'sheathed';
  }

  /** Swinging slows the feet but never plants them. */
  get locksMovement(): boolean {
    return this.phase === 'windup' || this.phase === 'swing' || (this.phase === 'draw' && !this.drawOnly);
  }

  /** Facing may still turn toward the stick while winding up. */
  get canAim(): boolean {
    return this.phase === 'windup' || this.phase === 'swing' || (this.phase === 'draw' && this.time < DRAW_REACH);
  }

  /** True during the fast part of a swing: the blade trails light and cuts grass. */
  get cutting(): boolean {
    if (this.phase !== 'swing') return false;
    const u = this.time / HITS[this.hit].swing;
    return u > 0.08 && u < 0.66;
  }

  /** In a swing (not a windup or the draw): the only time the blade leaves a trail. */
  get swinging(): boolean {
    return this.phase === 'swing';
  }

  get currentHit(): number {
    return this.hit + 1;
  }

  /** Stance depth 0..1 for the legs. */
  get crouch(): number {
    return this.pose.body.z * this.bodyW;
  }

  press(): void {
    const t = this.time;
    switch (this.phase) {
      case 'sheathed':
        this.beginDraw(false);
        break;
      case 'present':
        this.queued = true;
        break;
      case 'ready':
        this.beginHit(this.readyTime < CHAIN_WINDOW ? this.chain : 0);
        break;
      case 'recover':
        if (this.hit < HITS.length - 1) this.beginHit(this.hit + 1);
        else if (t > HITS[this.hit].recover * 0.2) this.beginHit(0);
        break;
      case 'windup':
        this.queued = true;
        break;
      case 'swing':
        if (t / HITS[this.hit].swing >= QUEUE_FROM) this.queued = true;
        break;
      case 'draw':
        if (this.drawOnly) this.queued = true;
        break;
      case 'sheathe':
        if (t < SHEATHE_SEAT - 0.06) this.beginHit(0);
        else if (t > SHEATHE_SEAT) this.beginDraw(false);
        break;
      default:
        break;
    }
  }

  /** Put the sword away now (e.g. before bowing at a shrine). */
  sheathe(): void {
    if (this.phase === 'ready' || this.phase === 'recover' || this.phase === 'present') this.beginSheathe();
  }

  /** Equip key: draw into the held guard, or put the sword away. */
  toggle(): void {
    if (this.phase === 'sheathed') this.beginDraw(true);
    else if (this.phase === 'sheathe') {
      if (this.time > SHEATHE_SEAT) this.beginDraw(true);
    } else if (this.phase === 'ready' || this.phase === 'recover' || this.phase === 'present') this.beginSheathe();
  }

  private beginDraw(drawOnly: boolean): void {
    this.drawOnly = drawOnly;
    this.phase = 'draw';
    this.time = 0;
    this.hit = 0;
    this.queued = false;
    this.attach = 0;
    this.slide = 0;
    this.blade.visible = true;
    this.events.push({ type: 'draw', clearIn: DRAW_REACH + DRAW_PULL });
  }

  private beginHit(index: number): void {
    this.start.copy(this.pose);
    this.phase = 'windup';
    this.time = 0;
    this.hit = index;
    this.queued = false;
    const h = HITS[index];
    this.fromSaya = this.attach < 1;
    this.windupTime = this.fromSaya ? DRAW_WINDUP : h.windup;
    this.events.push({ type: 'swing', hit: index + 1, contactIn: this.windupTime + CONTACT_U * h.swing });
  }

  private beginSheathe(): void {
    this.start.copy(this.pose);
    this.phase = 'sheathe';
    this.time = 0;
    this.events.push({ type: 'sheathe', seatIn: SHEATHE_SEAT });
  }

  /** Advance the state machine and choose the target pose. Call after the mixer, before `apply`. */
  update(dt: number, ctx: SwordContext): void {
    this.time += dt;
    this.spinYaw = 0;
    this.hiltMix = 0;
    this.held.blend(this.readyPose, this.runPose, smoothstep(1.2, 3.8, ctx.speed));
    let bodyTarget = 0;
    let armTarget = 1;

    switch (this.phase) {
      case 'sheathed':
        armTarget = 0;
        this.attach = 0;
        this.slide = 0;
        this.hiltMix = 1;
        this.sayaDrop = damp(this.sayaDrop, 0, 10, dt);
        break;

      case 'draw': {
        const t = this.time;
        this.hiltMix = 1;
        this.pose.body.set(-0.12 * smoothstep(0, DRAW_REACH, t), 0, 0.25);
        this.pose.leftW = 0;
        this.attach = 0;
        bodyTarget = 1;
        if (t < DRAW_REACH) {
          this.slide = 0;
          this.sayaDrop = smoothstep(0.03, DRAW_REACH, t) * 0.4;
          armTarget = smoothstep(0, DRAW_REACH * 0.8, t);
          this.armW = armTarget;
        } else {
          const k = easeInOut((t - DRAW_REACH) / DRAW_PULL);
          this.slide = DRAW_OUT * k;
          this.sayaDrop = lerp(0.4, 1, smoothstep(0, 0.5, k));
          this.armW = 1;
        }
        if (t >= DRAW_REACH + DRAW_PULL && this.drawOnly) {
          this.slide = DRAW_OUT;
          this.start.copy(this.pose);
          this.phase = 'present';
          this.time = 0;
        } else if (t >= DRAW_REACH + DRAW_PULL) {
          this.slide = DRAW_OUT;
          this.phase = 'windup';
          this.time = 0;
          this.fromSaya = true;
          this.windupTime = DRAW_WINDUP;
          this.events.push({ type: 'swing', hit: 1, contactIn: DRAW_WINDUP + CONTACT_U * KESA.swing });
        }
        break;
      }

      case 'present': {
        // Leave the saya axis and settle into the guard.
        const k = easeInOut(this.time / PRESENT_TIME);
        this.pose.copy(this.held);
        this.pose.body.lerpVectors(this.start.body, this.held.body, k);
        this.hiltMix = 1 - k;
        this.attach = smoothstep(0, 1, k);
        this.sayaDrop = damp(this.sayaDrop, 0, 6, dt);
        bodyTarget = 1 - k;
        if (this.time >= PRESENT_TIME) {
          this.attach = 1;
          this.phase = 'ready';
          this.readyTime = CHAIN_WINDOW;
          if (this.queued) this.beginHit(0);
        }
        break;
      }

      case 'windup': {
        const h = HITS[this.hit];
        const dur = this.windupTime;
        swingPose(h, 0, this.target);
        const k = easeInOut(this.time / (dur * 0.85));
        if (this.fromSaya) {
          // Leave the saya axis and settle into the hand on the way to the windup.
          this.pose.copy(this.target);
          this.pose.body.lerpVectors(this.start.body, this.target.body, k);
          this.hiltMix = 1 - k;
          this.attach = smoothstep(0, 1, k);
        } else {
          this.pose.blend(this.start, this.target, k);
        }
        this.sayaDrop = damp(this.sayaDrop, 0, 6, dt);
        bodyTarget = 1;
        if (this.time >= dur) {
          this.attach = 1;
          this.phase = 'swing';
          this.time = 0;
        }
        break;
      }

      case 'swing': {
        const h = HITS[this.hit];
        const u = this.time / h.swing;
        const g = swingProfile(u);
        swingPose(h, g, this.pose);
        this.spinYaw = h.spin * g;
        this.sayaDrop = damp(this.sayaDrop, 0, 6, dt);
        bodyTarget = 1;
        if (u >= CONTACT_U && u - dt / h.swing < CONTACT_U) this.events.push({ type: 'contact', hit: this.hit + 1 });
        if (u >= 1) {
          this.spinYaw = 0;
          if (this.queued) {
            this.beginHit((this.hit + 1) % HITS.length);
          } else {
            this.start.copy(this.pose);
            this.phase = 'recover';
            this.time = 0;
            this.chain = (this.hit + 1) % HITS.length;
          }
        }
        break;
      }

      case 'recover': {
        const h = HITS[this.hit];
        // Moving hurries the settle so locomotion takes over quickly.
        if (ctx.speed > 0.5) this.time += dt * 1.2;
        this.pose.blend(this.start, this.held, easeInOut(this.time / h.recover));
        bodyTarget = 1 - smoothstep(0.2, 1, this.time / h.recover);
        if (this.time >= h.recover) {
          this.phase = 'ready';
          this.readyTime = 0;
          if (this.hit === HITS.length - 1) this.chain = 0;
        }
        break;
      }

      case 'ready':
        this.readyTime += dt;
        this.pose.copy(this.held);
        // Breathing sway so the held blade never freezes.
        this.pose.grip.y += Math.sin(this.readyTime * 2.1) * 0.006;
        if (this.readyTime > this.autoSheathe && !ctx.flipping) this.beginSheathe();
        break;

      case 'sheathe':
        this.updateSheathe();
        armTarget = this.armW;
        bodyTarget = 0.5;
        break;
    }

    if (this.phase !== 'sheathe' && this.phase !== 'draw') this.armW = damp(this.armW, armTarget, this.phase === 'sheathed' ? 9 : 14, dt);
    this.bodyW = damp(this.bodyW, bodyTarget, 10, dt);
  }

  private updateSheathe(): void {
    const t = this.time;
    this.pose.leftW = 0;
    if (t < SHEATHE_LINEUP) {
      // Raise the blade, carry it tip-first over the right shoulder, and line it up with the saya mouth.
      const u = easeInOut(t / SHEATHE_LINEUP);
      const s0 = SHEATHE_KEYS[0] / SHEATHE_LINEUP;
      const s1 = (SHEATHE_KEYS[0] + SHEATHE_KEYS[1]) / SHEATHE_LINEUP;
      if (u < s0) {
        this.pose.blend(this.start, this.raisedPose, u / s0);
      } else if (u < s1) {
        this.pose.blend(this.raisedPose, this.overPose, (u - s0) / (s1 - s0));
      } else {
        const k = (u - s1) / (1 - s1);
        this.pose.copy(this.overPose);
        this.pose.body.set(-0.12, 0, 0.2);
        this.hiltMix = k;
        this.slide = DRAW_OUT;
        this.sayaDrop = smoothstep(0, 1, k);
        this.attach = 1 - smoothstep(0.55, 1, k);
      }
      this.armW = 1;
    } else if (t < SHEATHE_SEAT) {
      this.slide = DRAW_OUT * (1 - easeIn((t - SHEATHE_LINEUP) / SHEATHE_SLIDE));
      this.hiltMix = 1;
      this.attach = 0;
      this.sayaDrop = 1;
      this.armW = 1;
    } else {
      if (this.slide > 0) {
        this.slide = 0;
        this.blade.visible = false;
        this.events.push({ type: 'seat' });
      }
      this.hiltMix = 1;
      const k = (t - SHEATHE_SEAT) / SHEATHE_RELEASE;
      this.sayaDrop = 1 - smoothstep(0, 1, k);
      this.armW = 1 - smoothstep(0.1, 1, k);
      this.attach = 0;
      if (k >= 1) {
        this.phase = 'sheathed';
        this.armW = 0;
        this.chain = 0;
      }
    }
  }

  /** Model-space pose gripping the hilt with the sword slid `slide` metres out along the saya axis. */
  private hilt(slide: number, out: Pose): Pose {
    _m.copy(this.model.matrixWorld).invert().multiply(this.saya.matrixWorld);
    _m.decompose(_p, _q, _s);
    _y.set(0, 1, 0).applyQuaternion(_q);
    out.quat.copy(_q);
    out.grip.copy(_p).addScaledVector(_y, slide + GRIP_TO_TSUBA);
    out.pole.set(-0.5, 0.6, 0.6).normalize();
    return out;
  }

  /**
   * Write bones and the sword transform. Call after `root.updateMatrixWorld(true)`; leaves every
   * touched subtree's world matrices current.
   */
  apply(): void {
    const b = this.bones;
    // The saya dips along its own axis while the blade clears it.
    this.saya.position.copy(this.sayaRest);
    this.saya.translateY(-SAYA_DROP * this.sayaDrop);

    const w = this.bodyW;
    if (w > 0.001) {
      const tw = this.pose.body.x * w;
      const bd = this.pose.body.y * w;
      for (const [name, k] of [
        ['Spine', 0.3],
        ['Spine1', 0.35],
        ['Spine2', 0.35],
      ] as const) {
        const bone = b[name];
        if (!bone) continue;
        _q.setFromEuler(_euler.set(bd * k, tw * k, 0, 'YXZ'));
        bone.quaternion.multiply(_q);
      }
      // Keep the gaze on the cut.
      if (b.Neck) {
        _q.setFromEuler(_euler.set(-bd * 0.4, -tw * 0.55, 0, 'YXZ'));
        b.Neck.quaternion.multiply(_q);
      }
    }
    b.Hips.updateMatrixWorld(true);

    if (this.hiltMix > 0.001) {
      this.hilt(this.slide, this.target);
      this.pose.grip.lerp(this.target.grip, this.hiltMix);
      this.pose.quat.slerp(this.target.quat, this.hiltMix);
      this.pose.pole.lerp(this.target.pole, this.hiltMix).normalize();
    }

    this.model.matrixWorld.decompose(this.modelPos, this.modelQuat, _s);
    const armW = this.armW;
    if (armW > 0.001) {
      // Desired sword pose in world space, then the hand pose that grips it there.
      _q2.copy(this.modelQuat).multiply(this.pose.quat);
      _tip.set(0, -1, 0).applyQuaternion(this.pose.quat);
      _p.copy(this.pose.grip).addScaledVector(_tip, GRIP_TO_TSUBA).applyMatrix4(this.model.matrixWorld);
      _q3.copy(_q2).multiply(GRIP_QUAT_INV);
      _p2.copy(GRIP_POS).applyQuaternion(_q3);
      _p2.subVectors(_p, _p2);
      // Shrug the right clavicle when the hand goes high.
      const high = clamp((this.pose.grip.y - 1.45) / 0.45, 0, 1) * armW;
      if (b.RightShoulder && high > 0) {
        _q.setFromAxisAngle(_z.set(0, 0, 1), -0.32 * high);
        b.RightShoulder.quaternion.multiply(_q);
        b.RightShoulder.updateMatrixWorld(true);
      }
      _pole.copy(this.pose.pole).applyQuaternion(this.modelQuat);
      this.solveArm('Right', _p2, _q3, _pole, armW);
      this.curlFingers('Right', armW);
    }

    const leftW = this.pose.leftW * this.bodyW;
    if (leftW > 0.001 && b.LeftArm) {
      _p2.copy(this.pose.left).applyMatrix4(this.model.matrixWorld);
      _pole.set(0.6, -0.7, -0.3).applyQuaternion(this.modelQuat);
      this.solveArm('Left', _p2, null, _pole, leftW);
      this.curlFingers('Left', leftW * 0.8);
    }

    this.placeSword();
  }

  private solveArm(side: 'Right' | 'Left', wrist: THREE.Vector3, handQuat: THREE.Quaternion | null, poleHint: THREE.Vector3, weight: number): void {
    const arm = this.bones[`${side}Arm`];
    const fore = this.bones[`${side}ForeArm`];
    const hand = this.bones[`${side}Hand`];
    if (!arm || !fore || !hand || !arm.parent) return;
    const S = _s.setFromMatrixPosition(arm.matrixWorld);
    const E = _e.setFromMatrixPosition(fore.matrixWorld);
    const L1 = E.distanceTo(S);
    const L2 = _u.setFromMatrixPosition(hand.matrixWorld).distanceTo(E);
    _dir.subVectors(wrist, S);
    const d = clamp(_dir.length(), Math.abs(L1 - L2) + 1e-3, (L1 + L2) * 0.998);
    _dir.normalize();
    const hint = poleHint.dot(_dir);
    _pole.copy(poleHint).addScaledVector(_dir, -hint);
    if (_pole.lengthSq() < 1e-6) _pole.set(0, -1, 0).addScaledVector(_dir, _dir.y);
    _pole.normalize();
    const a = (L1 * L1 - L2 * L2 + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(L1 * L1 - a * a, 0));
    E.copy(S).addScaledVector(_dir, a).addScaledVector(_pole, h);
    const T = _p.copy(S).addScaledVector(_dir, d);
    _u.subVectors(E, S).normalize();
    _f.subVectors(T, E).normalize();
    // Elbow hinge: local +Y on the right chain, -Y on the left; both flex the forearm toward the palm.
    _n.crossVectors(_pole, _dir).normalize();
    const sign = side === 'Right' ? -1 : 1;
    const basis = (along: THREE.Vector3, out: THREE.Quaternion) => {
      _x.copy(along).multiplyScalar(sign);
      _y.copy(_n).multiplyScalar(-sign);
      _z.crossVectors(_x, _y);
      _m2.makeBasis(_x, _y, _z);
      return out.setFromRotationMatrix(_m2);
    };
    basis(_u, _qa);
    basis(_f, _qf);

    arm.parent.matrixWorld.decompose(_t0, _q, _t1);
    const parentQ = _q2.copy(_q);
    _q.copy(parentQ).invert().multiply(_qa);
    arm.quaternion.slerp(_q, weight);
    _qa.copy(parentQ).multiply(arm.quaternion);
    _q.copy(_qa).invert().multiply(_qf);
    fore.quaternion.slerp(_q, weight);
    if (handQuat) {
      _qf.copy(_qa).multiply(fore.quaternion);
      _q.copy(_qf).invert().multiply(handQuat);
      hand.quaternion.slerp(_q, weight);
    }
    arm.updateMatrixWorld(true);
  }

  private curlFingers(side: 'Right' | 'Left', weight: number): void {
    const sign = side === 'Right' ? 1 : -1;
    for (const finger of FINGERS) {
      const c = CURL[finger];
      for (let j = 0; j < 3; j++) {
        const bone = this.bones[`${side}Hand${finger}${j + 1}`];
        if (!bone) continue;
        _q.setFromAxisAngle(_z.set(0, 0, 1), c[j] * weight * sign);
        bone.quaternion.multiply(_q);
      }
    }
    for (let j = 1; j <= 3; j++) {
      const bone = this.bones[`${side}HandThumb${j}`];
      if (!bone) continue;
      _q.setFromAxisAngle(_z.set(0, 0, 1), (j === 1 ? 0.25 : 0.45) * weight * sign);
      bone.quaternion.multiply(_q);
    }
    this.bones[`${side}Hand`]?.updateMatrixWorld(true);
  }

  private placeSword(): void {
    // Where the saya holds it, slid out by `slide`.
    this.saya.matrixWorld.decompose(this.socketPos, this.socketQuat, _s);
    _y.set(0, 1, 0).applyQuaternion(this.socketQuat);
    this.socketPos.addScaledVector(_y, this.slide);
    if (this.attach > 0.001 && this.armW > 0.001) {
      this.bones.RightHand.matrixWorld.decompose(_p, _q, _s);
      _p.add(_p2.copy(GRIP_POS).applyQuaternion(_q));
      _q.multiply(GRIP_QUAT);
      this.swordPos.lerpVectors(this.socketPos, _p, this.attach);
      this.swordRot.slerpQuaternions(this.socketQuat, _q, this.attach);
    } else {
      this.swordPos.copy(this.socketPos);
      this.swordRot.copy(this.socketQuat);
    }
    this.swordMatrix.compose(this.swordPos, this.swordRot, _one);
    // The sword is a child of the root; store its root-local matrix.
    this.sword.matrix.copy(this.root.matrixWorld).invert().multiply(this.swordMatrix);
    this.sword.updateMatrixWorld(true);

    this.prevTip.copy(this.tip);
    this.prevBase.copy(this.base);
    this.tip.set(0, -(BLADE_TIP - 0.012), soriAt(BLADE_TIP) - 0.008).applyMatrix4(this.swordMatrix);
    this.base.set(0, -0.18, soriAt(0.18)).applyMatrix4(this.swordMatrix);
    if (!this.hasPrev) {
      this.prevTip.copy(this.tip);
      this.prevBase.copy(this.base);
      this.hasPrev = true;
    }
  }

  /** World-space blade speed at the tip (m/s), for trails and whoosh intensity. */
  measure(dt: number): void {
    this.tipSpeed = dt > 0 ? this.tip.distanceTo(this.prevTip) / dt : 0;
  }
}
